<script setup lang="ts">
import { useIntervalFn, usePreferredReducedMotion } from '@vueuse/core'

export type ReputationState = 'damaged' | 'pending' | 'healthy'

const props = withDefaults(defineProps<{ initialState?: ReputationState }>(), { initialState: 'pending' })

const { t } = useI18n()

const ARC_LENGTH = 251.33

const progress: Record<ReputationState, number> = {
  pending: 0.5,
  damaged: 0.22,
  healthy: 0.9
}

const knobClasses: Record<ReputationState, string> = {
  damaged: 'fill-red-600',
  pending: 'fill-orange-500',
  healthy: 'fill-green-600'
}

const haloClasses: Record<ReputationState, string> = {
  damaged: 'stroke-red-100',
  pending: 'stroke-orange-100',
  healthy: 'stroke-green-100'
}

const pillClasses: Record<ReputationState, string> = {
  damaged: 'bg-red-50 text-red-700',
  pending: 'bg-orange-50 text-orange-700',
  healthy: 'bg-green-50 text-green-700'
}

const cycle: ReputationState[] = ['pending', 'healthy', 'damaged']

const activeState = ref<ReputationState>(props.initialState)
const hasEntered = ref(false)
const reducedMotion = usePreferredReducedMotion()

const { pause } = useIntervalFn(
  () => {
    activeState.value = cycle[(cycle.indexOf(activeState.value) + 1) % cycle.length]
  },
  3400,
  { immediate: reducedMotion.value !== 'reduce' }
)

watch(reducedMotion, (value) => {
  if (value === 'reduce') pause()
})

onMounted(() => {
  const timer = setTimeout(() => {
    hasEntered.value = true
  }, 80)

  onUnmounted(() => clearTimeout(timer))
})

const fillStyle = computed(() => ({
  strokeDasharray: `${ARC_LENGTH}`,
  strokeDashoffset: `${ARC_LENGTH * (hasEntered.value ? 1 - progress[activeState.value] : 1)}`
}))

const knobStyle = computed(() => ({
  transform: `rotate(${hasEntered.value ? progress[activeState.value] * 180 : 0}deg)`
}))
</script>

<template>
  <div class="flex flex-col items-center">
    <svg
      viewBox="0 0 220 126"
      class="w-[150px]"
      role="img"
      :aria-label="t('pages.campaignEmail.firstSend.gauge.ariaLabel')"
    >
      <defs>
        <linearGradient id="reputation-damaged" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stop-color="#FCA5A5" />
          <stop offset="100%" stop-color="#DC2626" />
        </linearGradient>
        <linearGradient id="reputation-pending" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stop-color="#FDBA74" />
          <stop offset="100%" stop-color="#EA580C" />
        </linearGradient>
        <linearGradient id="reputation-healthy" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stop-color="#86EFAC" />
          <stop offset="100%" stop-color="#16A34A" />
        </linearGradient>
      </defs>

      <path
        d="M 30 100 A 80 80 0 0 1 190 100"
        fill="none"
        stroke-width="18"
        stroke-linecap="round"
        class="stroke-muted"
      />
      <path
        d="M 30 100 A 80 80 0 0 1 190 100"
        fill="none"
        stroke-width="32"
        stroke-linecap="round"
        :style="fillStyle"
        class="transition-[stroke-dashoffset] duration-1000 ease-[cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none"
        :class="haloClasses[activeState]"
      />
      <path
        d="M 30 100 A 80 80 0 0 1 190 100"
        fill="none"
        stroke-width="18"
        stroke-linecap="round"
        :stroke="`url(#reputation-${activeState})`"
        :style="fillStyle"
        class="transition-[stroke-dashoffset] duration-1000 ease-[cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none"
      />

      <g
        class="origin-[110px_100px] transition-transform duration-1000 ease-[cubic-bezier(0.32,0.72,0,1)] [transform-box:view-box] motion-reduce:transition-none"
        :style="knobStyle"
      >
        <circle cx="30" cy="100" r="12.5" class="fill-card stroke-border" stroke-width="1" />
        <circle cx="30" cy="100" r="8.5" class="transition-colors duration-700" :class="knobClasses[activeState]" />
      </g>
    </svg>

    <span
      class="-mt-1 rounded-full px-3 py-1 text-xs font-medium transition-colors duration-700"
      :class="pillClasses[activeState]"
    >
      {{ t(`pages.campaignEmail.firstSend.gauge.states.${activeState}.label`) }}
    </span>
  </div>
</template>
